export type AnimalRole = 'vertical' | 'horizontal' | 'rounded' | 'unstable';
export type RequiredAnimalRole = 'meerkat';
export type AnimalVariantTier = 'NORMAL' | 'RARE' | 'SUPER_RARE';

export const ANIMAL_SIZE_SCALE = 0.8;

export type AnimalCenterOfMassDefinition =
  | { mode: 'auto' }
  | {
      mode: 'authored';
      sourceNormalized: { x: number; y: number };
      note: string;
    };

interface CollisionPartBase {
  id?: string;
  x: number;
  y: number;
}

export type CollisionPart = CollisionPartBase & (
  | { kind: 'rectangle'; width: number; height: number; angle?: number }
  | { kind: 'circle'; radius: number }
  | { kind: 'polygon'; vertices: readonly { x: number; y: number }[] }
);

export interface AnimalDefinition {
  id: string;
  name: string;
  shortLabel: string;
  role: AnimalRole;
  requiredRole?: RequiredAnimalRole;
  species?: string;
  variantTier?: AnimalVariantTier;
  variantId?: string;
  width: number;
  height: number;
  density: number;
  friction: number;
  frictionStatic: number;
  frictionAir: number;
  restitution: number;
  baseScore: number;
  selectionWeight: number;
  palette: { body: string; shade: string; accent: string };
  parts: CollisionPart[];
  markings?: 'spots' | 'stripes' | 'scales' | 'plain';
  visual?: AnimalVisualDefinition;
  texture?: AnimalTextureDefinition;
  hitboxMask?: HitboxMaskDefinition;
  visualCenter?: { x: number; y: number };
  centerOfMass: AnimalCenterOfMassDefinition;
}

export interface HitboxMaskDefinition {
  path: string;
  sourceWidth: number;
  sourceHeight: number;
}

interface AnimalTextureBase {
  sourceWidth: number;
  sourceHeight: number;
  displayScale: { x: number; y: number };
}

export type AnimalTextureDefinition =
  | (AnimalTextureBase & { kind: 'image'; path: string })
  | (AnimalTextureBase & {
    kind: 'atlas';
    imagePath: string;
    atlasPath: string;
    frame: string;
  });

export interface AnimalVisualDefinition {
  body: { cx: number; cy: number; rx: number; ry: number };
  head: { cx: number; cy: number; rx: number; ry: number };
  eye: { cx: number; cy: number };
  legs: readonly { x: number; y: number; width: number; height: number }[];
  tail: string;
  ear?: { cx: number; cy: number; rx: number; ry: number };
  neck?: { x: number; y: number; width: number; height: number; rotate: number };
  trunk?: string;
}
